/**
 * HabitScheduleEditor
 * ===================
 *
 * Google-Calendar-style recurrence editor for habits. Emits a canonical
 * RRULE string + array of local time-of-day strings + start date.
 *
 * Inputs the editor exposes:
 *   - Frequency (Daily / Weekly / Monthly / Yearly) + interval
 *   - Weekly: BYDAY toggle chips (Mon–Sun)
 *   - Monthly: day-of-month OR "nth weekday of the month"
 *   - Starts on: date picker
 *   - Time(s) of day: one or more HH:MM rows (1-minute step, 24h)
 *   - Ends: Never / On date / After N occurrences
 *   - Duration (minutes)
 *
 * The component is fully controlled — parent supplies `value` and
 * handles `onChange`. The value shape is small enough that parent can
 * spread it into the habit CRUD payload directly.
 */

import React, { useMemo } from 'react'
import { Plus, Trash2, Clock, CalendarDays } from 'lucide-react'
import {
  buildRRule,
  parseRRule,
  describeRRule,
  defaultSpec,
  deriveLegacyCadence,
  deriveLegacyPreferredDays,
  deriveLegacyTimeWindow,
  WEEKDAYS,
  type RRuleSpec,
  type RWeekday,
  type Frequency,
  type MonthlyMode,
  type EndMode,
} from '../../utils/rrule'

export interface HabitScheduleValue {
  rrule: string
  scheduled_times_local: string[]
  schedule_start_date: string   // YYYY-MM-DD
  duration_minutes: number
  // Derived legacy mirrors so existing read paths keep working:
  target_cadence: string
  preferred_days: string[]
  preferred_time_window: string
  // Follow-through / nag settings
  nag_enabled: boolean
  follow_through_mode: 'hard_window' | 'soft_window' | 'all_day'
  nag_window_minutes: number
  nag_interval_minutes: number
  nag_max_per_window: number
  nag_style: 'gentle' | 'steady' | 'persistent' | 'strict'
}

interface HabitScheduleEditorProps {
  value: HabitScheduleValue
  onChange: (next: HabitScheduleValue) => void
  /** The habit page edits reminders as their own fact; it passes false here. */
  showFollowThrough?: boolean
}

/** Today in YYYY-MM-DD local. */
function todayLocalISO(): string {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

export function makeDefaultScheduleValue(): HabitScheduleValue {
  const spec = defaultSpec()
  return {
    rrule: buildRRule(spec),
    scheduled_times_local: ['08:00'],
    schedule_start_date: todayLocalISO(),
    duration_minutes: 30,
    target_cadence: 'daily',
    preferred_days: [],
    preferred_time_window: 'morning',
    nag_enabled: true,
    follow_through_mode: 'hard_window',
    nag_window_minutes: 60,
    nag_interval_minutes: 15,
    nag_max_per_window: 4,
    nag_style: 'persistent',
  }
}

/** Rebuild derived fields when spec/times change. */
function recompose(
  spec: RRuleSpec,
  times: string[],
  start: string,
  duration: number,
  followThrough?: Pick<HabitScheduleValue, 'nag_enabled' | 'follow_through_mode' | 'nag_window_minutes' | 'nag_interval_minutes' | 'nag_max_per_window' | 'nag_style'>,
): HabitScheduleValue {
  return {
    rrule: buildRRule(spec),
    scheduled_times_local: times.slice().sort(),
    schedule_start_date: start,
    duration_minutes: duration,
    target_cadence: deriveLegacyCadence(spec),
    preferred_days: deriveLegacyPreferredDays(spec),
    preferred_time_window: deriveLegacyTimeWindow(times[0] || '08:00'),
    nag_enabled: followThrough?.nag_enabled ?? true,
    follow_through_mode: followThrough?.follow_through_mode ?? 'hard_window',
    nag_window_minutes: followThrough?.nag_window_minutes ?? 60,
    nag_interval_minutes: followThrough?.nag_interval_minutes ?? 15,
    nag_max_per_window: followThrough?.nag_max_per_window ?? 4,
    nag_style: followThrough?.nag_style ?? 'persistent',
  }
}

export const HabitScheduleEditor: React.FC<HabitScheduleEditorProps> = ({ value, onChange, showFollowThrough = true }) => {
  const spec: RRuleSpec = useMemo(() => parseRRule(value.rrule), [value.rrule])
  const [showAdvanced, setShowAdvanced] = React.useState(false)

  const ftFields = () => ({
    nag_enabled: value.nag_enabled ?? true,
    follow_through_mode: value.follow_through_mode ?? 'hard_window' as const,
    nag_window_minutes: value.nag_window_minutes ?? 60,
    nag_interval_minutes: value.nag_interval_minutes ?? 15,
    nag_max_per_window: value.nag_max_per_window ?? 4,
    nag_style: value.nag_style ?? 'persistent' as const,
  })

  const update = (nextSpec: RRuleSpec, nextTimes?: string[], nextStart?: string, nextDuration?: number) => {
    onChange(
      recompose(
        nextSpec,
        nextTimes ?? value.scheduled_times_local,
        nextStart ?? value.schedule_start_date,
        nextDuration ?? value.duration_minutes,
        ftFields(),
      ),
    )
  }

  const updateFT = (patch: Partial<ReturnType<typeof ftFields>>) => {
    onChange(
      recompose(
        spec,
        value.scheduled_times_local,
        value.schedule_start_date,
        value.duration_minutes,
        { ...ftFields(), ...patch },
      ),
    )
  }

  const setFreq = (freq: Frequency) => {
    const next: RRuleSpec = { ...spec, freq }
    // Reasonable defaults when switching families
    if (freq === 'WEEKLY' && (!next.byweekday || next.byweekday.length === 0)) {
      next.byweekday = ['MO', 'WE', 'FR']
    }
    if (freq === 'MONTHLY' && !next.monthlyMode) {
      next.monthlyMode = 'day_of_month'
      next.bymonthday = new Date(value.schedule_start_date || todayLocalISO()).getDate() || 1
    }
    update(next)
  }

  const toggleWeekday = (code: RWeekday) => {
    const cur = new Set(spec.byweekday || [])
    if (cur.has(code)) cur.delete(code)
    else cur.add(code)
    update({ ...spec, byweekday: Array.from(cur) })
  }

  const setInterval = (n: number) => update({ ...spec, interval: Math.max(1, n) })

  const setMonthlyMode = (mode: MonthlyMode) => {
    const next: RRuleSpec = { ...spec, monthlyMode: mode }
    if (mode === 'day_of_month' && !next.bymonthday) {
      next.bymonthday = new Date(value.schedule_start_date || todayLocalISO()).getDate() || 1
    }
    if (mode === 'nth_weekday') {
      if (!next.bysetpos) next.bysetpos = 1
      if (!next.bydayForMonthly) next.bydayForMonthly = 'MO'
    }
    update(next)
  }

  const setEndMode = (mode: EndMode) => {
    const next: RRuleSpec = { ...spec, endMode: mode }
    if (mode === 'after_count' && !next.count) next.count = 10
    if (mode === 'on_date' && !next.until) {
      // Default UNTIL: 90 days from start
      const d = new Date(value.schedule_start_date || todayLocalISO())
      d.setDate(d.getDate() + 90)
      next.until = d.toISOString().slice(0, 10)
    }
    if (mode === 'never') {
      delete next.count
      delete next.until
    }
    update(next)
  }

  const addTime = () => {
    const times = [...value.scheduled_times_local, '18:00']
    update(spec, times)
  }

  const removeTime = (idx: number) => {
    const times = value.scheduled_times_local.filter((_, i) => i !== idx)
    update(spec, times.length > 0 ? times : ['08:00'])
  }

  const setTime = (idx: number, hhmm: string) => {
    const times = value.scheduled_times_local.slice()
    times[idx] = hhmm
    update(spec, times)
  }

  const summary = useMemo(
    () => describeRRule(spec, value.scheduled_times_local),
    [spec, value.scheduled_times_local],
  )

  return (
    <div className="space-y-4">
      {/* Frequency + interval */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="sm:col-span-2">
          <label className="block text-sm font-medium text-secondary mb-1">
            Repeats
          </label>
          <select
            value={spec.freq}
            onChange={(e) => setFreq(e.target.value as Frequency)}
            className="w-full rounded-md border border-soft bg-surface p-2 text-sm"
          >
            <option value="DAILY">Daily</option>
            <option value="WEEKLY">Weekly</option>
            <option value="MONTHLY">Monthly</option>
            <option value="YEARLY">Yearly</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-secondary mb-1">
            Every
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min={1}
              max={99}
              value={spec.interval}
              onChange={(e) => setInterval(parseInt(e.target.value, 10) || 1)}
              className="w-16 rounded-md border border-soft bg-surface p-2 text-sm"
            />
            <span className="text-sm text-secondary">
              {spec.freq === 'DAILY' && (spec.interval === 1 ? 'day' : 'days')}
              {spec.freq === 'WEEKLY' && (spec.interval === 1 ? 'week' : 'weeks')}
              {spec.freq === 'MONTHLY' && (spec.interval === 1 ? 'month' : 'months')}
              {spec.freq === 'YEARLY' && (spec.interval === 1 ? 'year' : 'years')}
            </span>
          </div>
        </div>
      </div>

      {/* Weekly: day-of-week chips */}
      {spec.freq === 'WEEKLY' && (
        <div>
          <label className="block text-sm font-medium text-secondary mb-2">
            Repeat on
          </label>
          <div className="flex flex-wrap gap-2">
            {WEEKDAYS.map((w) => {
              const selected = (spec.byweekday || []).includes(w.code)
              return (
                <button
                  key={w.code}
                  type="button"
                  onClick={() => toggleWeekday(w.code)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    selected
                      ? 'bg-brand text-on-brand border-brand'
                      : 'bg-surface text-secondary border-soft hover:border-brand'
                  }`}
                >
                  {w.short}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Monthly: day-of-month vs nth weekday */}
      {spec.freq === 'MONTHLY' && (
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-secondary">
            <input
              type="radio"
              name="monthlyMode"
              checked={spec.monthlyMode === 'day_of_month'}
              onChange={() => setMonthlyMode('day_of_month')}
            />
            <span>On day</span>
            <input
              type="number"
              min={1}
              max={31}
              value={spec.bymonthday ?? 1}
              onChange={(e) =>
                update({ ...spec, bymonthday: Math.min(31, Math.max(1, parseInt(e.target.value, 10) || 1)) })
              }
              disabled={spec.monthlyMode !== 'day_of_month'}
              className="w-16 rounded-md border border-soft bg-surface p-1 text-sm disabled:opacity-40"
            />
            <span>of the month</span>
          </label>
          <label className="flex items-center gap-2 text-sm text-secondary flex-wrap">
            <input
              type="radio"
              name="monthlyMode"
              checked={spec.monthlyMode === 'nth_weekday'}
              onChange={() => setMonthlyMode('nth_weekday')}
            />
            <span>On the</span>
            <select
              value={spec.bysetpos ?? 1}
              onChange={(e) => update({ ...spec, bysetpos: parseInt(e.target.value, 10) })}
              disabled={spec.monthlyMode !== 'nth_weekday'}
              className="rounded-md border border-soft bg-surface p-1 text-sm disabled:opacity-40"
            >
              <option value={1}>first</option>
              <option value={2}>second</option>
              <option value={3}>third</option>
              <option value={4}>fourth</option>
              <option value={-1}>last</option>
            </select>
            <select
              value={spec.bydayForMonthly ?? 'MO'}
              onChange={(e) => update({ ...spec, bydayForMonthly: e.target.value as RWeekday })}
              disabled={spec.monthlyMode !== 'nth_weekday'}
              className="rounded-md border border-soft bg-surface p-1 text-sm disabled:opacity-40"
            >
              {WEEKDAYS.map((w) => (
                <option key={w.code} value={w.code}>{w.long}</option>
              ))}
            </select>
            <span>of the month</span>
          </label>
        </div>
      )}

      {/* Start date */}
      <div>
        <label className="block text-sm font-medium text-secondary mb-1">
          <CalendarDays className="h-3.5 w-3.5 inline mr-1" />
          Starts
        </label>
        <input
          type="date"
          value={value.schedule_start_date}
          onChange={(e) => update(spec, undefined, e.target.value)}
          className="rounded-md border border-soft bg-surface p-2 text-sm"
        />
      </div>

      {/* Times of day */}
      <div>
        <label className="block text-sm font-medium text-secondary mb-2">
          <Clock className="h-3.5 w-3.5 inline mr-1" />
          Time{value.scheduled_times_local.length > 1 ? 's' : ''} of day
        </label>
        <div className="space-y-2">
          {value.scheduled_times_local.map((t, idx) => (
            <div key={idx} className="flex items-center gap-2">
              <input
                type="time"
                step={60}
                value={t}
                onChange={(e) => setTime(idx, e.target.value)}
                className="rounded-md border border-soft bg-surface p-2 text-sm"
              />
              {value.scheduled_times_local.length > 1 && (
                <button
                  type="button"
                  onClick={() => removeTime(idx)}
                  className="p-1.5 rounded-md text-bad hover:bg-bad/10"
                  aria-label="Remove time"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addTime}
            className="inline-flex items-center gap-1 text-xs font-medium text-brand hover:underline"
          >
            <Plus className="h-3.5 w-3.5" /> Add another time
          </button>
        </div>
      </div>

      {/* Duration */}
      <div>
        <label className="block text-sm font-medium text-secondary mb-1">
          Duration (minutes)
        </label>
        <input
          type="number"
          min={1}
          max={480}
          value={value.duration_minutes}
          onChange={(e) => update(spec, undefined, undefined, parseInt(e.target.value, 10) || 30)}
          className="w-24 rounded-md border border-soft bg-surface p-2 text-sm"
        />
      </div>

      {/* Ends */}
      <div>
        <label className="block text-sm font-medium text-secondary mb-2">
          Ends
        </label>
        <div className="space-y-2 text-sm text-secondary">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="endMode"
              checked={spec.endMode === 'never'}
              onChange={() => setEndMode('never')}
            />
            <span>Never</span>
          </label>
          <label className="flex items-center gap-2 flex-wrap">
            <input
              type="radio"
              name="endMode"
              checked={spec.endMode === 'on_date'}
              onChange={() => setEndMode('on_date')}
            />
            <span>On</span>
            <input
              type="date"
              value={spec.until ?? ''}
              onChange={(e) => update({ ...spec, until: e.target.value, endMode: 'on_date' })}
              disabled={spec.endMode !== 'on_date'}
              className="rounded-md border border-soft bg-surface p-1 text-sm disabled:opacity-40"
            />
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="endMode"
              checked={spec.endMode === 'after_count'}
              onChange={() => setEndMode('after_count')}
            />
            <span>After</span>
            <input
              type="number"
              min={1}
              max={999}
              value={spec.count ?? 10}
              onChange={(e) =>
                update({ ...spec, count: Math.max(1, parseInt(e.target.value, 10) || 1), endMode: 'after_count' })
              }
              disabled={spec.endMode !== 'after_count'}
              className="w-20 rounded-md border border-soft bg-surface p-1 text-sm disabled:opacity-40"
            />
            <span>occurrences</span>
          </label>
        </div>
      </div>

      {/* Follow-through settings */}
      {showFollowThrough && (
      <div className="border-t border-soft pt-4 mt-2">
        <div className="flex items-center justify-between mb-3">
          <label className="text-sm font-medium text-secondary">Follow-through reminders</label>
          <button
            type="button"
            onClick={() => updateFT({ nag_enabled: !value.nag_enabled })}
            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
              value.nag_enabled !== false ? 'bg-brand' : 'bg-gray-300'
            }`}
          >
            <span
              className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
                value.nag_enabled !== false ? 'translate-x-[18px]' : 'translate-x-[3px]'
              }`}
            />
          </button>
        </div>

        {value.nag_enabled !== false && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-secondary mb-1">Mode</label>
                <select
                  value={value.follow_through_mode || 'hard_window'}
                  onChange={(e) => updateFT({ follow_through_mode: e.target.value as HabitScheduleValue['follow_through_mode'] })}
                  className="w-full rounded-md border border-soft bg-surface p-2 text-sm"
                >
                  <option value="hard_window">Hard window (must do near time)</option>
                  <option value="soft_window">Soft window (flexible timing)</option>
                  <option value="all_day">All day (anytime today)</option>
                </select>
              </div>
              <div>
                <label className="block text-xs text-secondary mb-1">Style</label>
                <select
                  value={value.nag_style || 'persistent'}
                  onChange={(e) => updateFT({ nag_style: e.target.value as HabitScheduleValue['nag_style'] })}
                  className="w-full rounded-md border border-soft bg-surface p-2 text-sm"
                >
                  <option value="gentle">Gentle</option>
                  <option value="steady">Steady</option>
                  <option value="persistent">Persistent</option>
                  <option value="strict">Strict</option>
                </select>
              </div>
            </div>

            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-xs text-brand hover:underline"
            >
              {showAdvanced ? 'Hide advanced' : 'Advanced settings'}
            </button>

            {showAdvanced && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-secondary mb-1">Window (min)</label>
                  <input
                    type="number"
                    min={15}
                    max={120}
                    value={value.nag_window_minutes ?? 60}
                    onChange={(e) => updateFT({ nag_window_minutes: Math.max(15, Math.min(120, parseInt(e.target.value, 10) || 60)) })}
                    className="w-full rounded-md border border-soft bg-surface p-2 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs text-secondary mb-1">Interval (min)</label>
                  <select
                    value={value.nag_interval_minutes ?? 15}
                    onChange={(e) => updateFT({ nag_interval_minutes: parseInt(e.target.value, 10) })}
                    className="w-full rounded-md border border-soft bg-surface p-2 text-sm"
                  >
                    <option value={10}>Every 10 min</option>
                    <option value={15}>Every 15 min</option>
                    <option value={20}>Every 20 min</option>
                    <option value={30}>Every 30 min</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-secondary mb-1">Max nudges</label>
                  <input
                    type="number"
                    min={1}
                    max={8}
                    value={value.nag_max_per_window ?? 4}
                    onChange={(e) => updateFT({ nag_max_per_window: Math.max(1, Math.min(8, parseInt(e.target.value, 10) || 4)) })}
                    className="w-full rounded-md border border-soft bg-surface p-2 text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      )}

      {/* Live summary */}
      <div className="p-2.5 rounded-md bg-brand-subtle border border-brand-subtle/50">
        <div className="text-xs font-medium text-brand mb-0.5">Summary</div>
        <div className="text-sm text-brand">{summary}</div>
      </div>
    </div>
  )
}

export default HabitScheduleEditor
